import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { FlowerQueue } from '../lib/flowerQueue';
import type { FlowerReceipt } from '../types';
import { useOnlineStatus } from './useOnlineStatus';

interface UseFlowerQueueOptions {
  generation: number | null;
  onApplied(receipt: FlowerReceipt): void;
  onError(message: string): void;
}

export function useFlowerQueue({
  generation,
  onApplied,
  onError,
}: UseFlowerQueueOptions) {
  const queue = useMemo(() => new FlowerQueue(window.localStorage), []);
  const online = useOnlineStatus();
  const processing = useRef(false);
  const retryTimer = useRef<number | null>(null);
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const currentGeneration = useRef(generation);
  currentGeneration.current = generation;

  useEffect(
    () => queue.subscribe(() => setVersion((value) => value + 1)),
    [queue],
  );

  useEffect(() => {
    if (generation === null) return;
    if (queue.setGeneration(generation)) {
      onError('The meeting was reset. Pending flowers from the previous meeting were cleared.');
    }
  }, [generation, onError, queue]);

  const flush = useCallback(async () => {
    if (!online || generation === null || processing.current) return;
    const next = queue.takeNext();
    if (!next) return;

    processing.current = true;
    setSaving(true);
    try {
      const receipt = await api.applyFlowers(
        next.topicId,
        next.batchId,
        next.delta,
        next.generation ?? 0,
      );
      queue.acknowledge(next.batchId);
      if ((next.generation ?? 0) === currentGeneration.current) onApplied(receipt);
      processing.current = false;
      setSaving(false);
      void flush();
    } catch (error) {
      processing.current = false;
      setSaving(false);
      if (
        (next.generation ?? 0) !== currentGeneration.current ||
        (typeof error === 'object' && error !== null &&
          'code' in error && error.code === 'PT409')
      ) {
        queue.acknowledge(next.batchId);
        onError('The meeting was reset. Pending flowers from the previous meeting were cleared.');
        return;
      }
      onError('Flowers are saved on this device and will retry when connected.');
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(next.attempts, 5));
      retryTimer.current = window.setTimeout(() => void flush(), delay);
    }
  }, [generation, online, onApplied, onError, queue]);

  useEffect(() => {
    const interval = window.setInterval(() => void flush(), 5_000);
    if (online) void flush();
    return () => {
      window.clearInterval(interval);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
  }, [flush, online]);

  const addFlower = useCallback(
    (topicId: string) => {
      if (generation === null) throw new Error('Wait for the meeting to load before sending flowers.');
      queue.setGeneration(generation);
      queue.enqueue(topicId);
    },
    [generation, queue],
  );

  const pendingFor = useCallback(
    (topicId: string) => {
      void version;
      return queue.pendingFor(topicId);
    },
    [queue, version],
  );

  return {
    addFlower,
    flush,
    pendingFor,
    pendingCount: queue.snapshot().reduce((sum, batch) => sum + batch.delta, 0),
    saving,
  };
}
